/* How a stack is drawn on the map: a top-down group of small figures (owner, 2026-10-26).

   It replaces the one side-view sprite per composition (requirement 36, S1–S2), which at a 24px cell
   read as a badge rather than as troops, and drew five soldiers and five thousand the same. A stack
   is now a formation seen from above: 3 to 16 figures, more as the army grows (`figuresFor`, on a
   log scale — the number label says the size, the figures give the feel of it), each unit type its
   own figure and its own place in the group, the whole group turned to face where it marches.

   - Humanity marches in ranks: tanks in single file at the head, the infantry in ranks behind them
     (elites first, medics and workers last), helicopters over the middle with their shadow below.
   - The horde is a loose blob, never a grid: titans in the middle, the rest on a jittered disc,
     each figure tilted its own way; dogs run scattered ahead of it.
   - A stack the payload names no units for — `{count: n}` from a server older than 36/V1, which
     sent an enemy march as a headcount — is generic figures of the side: the map draws no type the
     server did not send.

   `formationFor` is pure and deterministic: the same composition is the same group for every
   player and every frame, and nothing reads a cosmetic (seam rule 5). `drawFormation` draws it in
   vector shapes about a centre, `time` animating the walk while `walking` is true. Under 14px a
   cell the figures are noise, so the group is drawn as dots. */

const HUMAN_INFANTRY = ["elite", "soldier", "ranger", "medic", "worker"]
const ZOMBIE_BODIES = ["destructor", "cursed", "zombie", "walker", "ambusher"]
// A heavy unit takes a larger share of the figures than of the bodies: 3 tanks beside 200 soldiers
// still show a tank.
const FIGURE_WEIGHT = { tank: 20, helicopter: 20, titan: 15 }
// Bigger than an infantry figure, in figures.
const FIGURE_SIZE = { tank: 1.9, helicopter: 2.0, titan: 2.1, destructor: 1.35, dog: 1.2 }
const SPACING = 1.15
export const MAX_FIGURES = 16
export const DOTS_BELOW_CELL_PX = 14
// The widest a group may be, in cells: a big group shrinks its figures rather than spill over its
// neighbours.
const MAX_SPAN_CELLS = 1.3

const countOf = (value) => {
  if (typeof value === "number") return value
  if (value && typeof value === "object") return Number(value.number ?? value.count ?? 0) || 0
  return 0
}

export const figuresFor = (bodies) => {
  if (bodies <= 0) return 0
  if (bodies < 10) return 3
  if (bodies < 100) return 5 + Math.round(2 * Math.log10(bodies))
  if (bodies < 1000) return 8 + Math.round(4 * (Math.log10(bodies) - 2))
  return Math.min(MAX_FIGURES, 12 + Math.round(4 * (Math.log10(bodies) - 3)))
}

/* Figures by type: at least one for every type present, never more than its bodies, the rest by
   weighted share and the largest remainders. */
export const allocateFigures = (bodies, figures) => {
  const entries = bodies.filter(([, n]) => n > 0)
  const weights = entries.map(([unit, n]) => n * (FIGURE_WEIGHT[unit] || 1))
  const total = weights.reduce((sum, w) => sum + w, 0)
  if (total === 0) return {}
  const shares = entries.map(([unit, n], i) => ({ unit, n, exact: (figures * weights[i]) / total }))
  const out = {}
  let used = 0
  shares.forEach(({ unit, n, exact }) => {
    out[unit] = Math.min(n, Math.max(1, Math.floor(exact)))
    used += out[unit]
  })
  const byRemainder = [...shares].sort((a, b) => b.exact - Math.floor(b.exact) - (a.exact - Math.floor(a.exact)))
  let grew = true
  while (used < figures && grew) {
    grew = false
    for (const { unit, n } of byRemainder) {
      if (used >= figures) break
      if (out[unit] < n) {
        out[unit] += 1
        used += 1
        grew = true
      }
    }
  }
  return out
}

/* A small seeded generator, so a composition jitters the same way on every screen. */
const seeded = (seed) => {
  let state = seed | 0
  return () => {
    state = (state + 0x6d2b79f5) | 0
    let t = Math.imul(state ^ (state >>> 15), 1 | state)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const seedOf = (text) => {
  let hash = 2166136261
  for (let i = 0; i < text.length; i += 1) hash = Math.imul(hash ^ text.charCodeAt(i), 16777619)
  return hash
}

/* `count` places in rows of `perRow` across, each row centred, the rows going back from u = 0:
   `[u, v]` in figure spacings. */
const block = (count, perRow, across, back) => {
  const places = []
  for (let i = 0; i < count; i += 1) {
    const row = Math.floor(i / perRow)
    const inRow = Math.min(perRow, count - row * perRow)
    places.push([-row * back, ((i % perRow) - (inRow - 1) / 2) * across])
  }
  return places
}

const cache = new Map()

/* The group a `units` dict is drawn as. `units` may be the board's `{unit: {number}}`, the motion
   log's `{unit: n}`, or the fog's `{count: n}` / nothing. Positions are in figure spacings about
   the group's centre: `u` forward, `v` to the right. */
export const formationFor = (units, team) => {
  const bodies = Object.entries(units || {})
    .filter(([unit]) => unit !== "count")
    .map(([unit, value]) => [unit, countOf(value)])
    .filter(([, n]) => n > 0)
  const headcount = bodies.length === 0
  const total = headcount ? countOf(units?.count) : bodies.reduce((sum, [, n]) => sum + n, 0)
  const cacheKey = `${team}|${headcount ? `count:${total}` : bodies.map(([u, n]) => `${u}:${n}`).sort().join(",")}`
  if (cache.has(cacheKey)) return cache.get(cacheKey)

  const figureCount = figuresFor(Math.max(1, total))
  const ghost = team === "zombie" ? "ghost_zombie" : "ghost_human"
  const allocation = headcount ? { [ghost]: figureCount } : allocateFigures(bodies, figureCount)
  const random = seeded(seedOf(cacheKey))
  const figures = []
  const place = (type, u, v, extra = {}) =>
    figures.push({ type, u, v, size: FIGURE_SIZE[type] || 1, phase: random() * Math.PI * 2, rate: 0.8 + random() * 0.5, ...extra })

  if (team === "zombie") {
    const titans = allocation.titan || 0
    const blob = []
    ;[...ZOMBIE_BODIES, ghost].forEach((type) => {
      for (let i = 0; i < (allocation[type] || 0); i += 1) blob.push(type)
    })
    const slots = blob.length + titans * 3
    const radius = Math.sqrt(slots) * 0.62
    for (let i = 0; i < titans; i += 1) {
      const angle = (i / titans) * Math.PI * 2
      const distance = titans > 1 ? radius * 0.35 : 0
      place("titan", Math.cos(angle) * distance, Math.sin(angle) * distance)
    }
    // A golden-angle disc with jitter: loose and uneven, never ranks.
    blob.forEach((type, i) => {
      const k = i + titans * 3
      const r = radius * Math.sqrt((k + 0.5) / slots)
      const angle = k * 2.399963 + random() * 0.6
      place(type, Math.cos(angle) * r + (random() - 0.5) * 0.4, Math.sin(angle) * r + (random() - 0.5) * 0.4, {
        tilt: (random() - 0.5) * 0.7,
      })
    })
    const dogs = allocation.dog || 0
    const front = blob.length + titans > 0 ? radius : 0
    for (let i = 0; i < dogs; i += 1) {
      place("dog", front + 0.6 + random() * 2.2, (random() - 0.5) * Math.min(5, 1.5 + dogs * 0.35), {
        rate: 1.6 + random() * 0.8,
        tilt: (random() - 0.5) * 0.5,
      })
    }
  } else {
    // The tanks lead as a block two or three abreast, never in single file: ten in a line made the
    // group twenty cells long and shrank every figure to fit it (owner, 2026-10-26).
    const tanks = allocation.tank || 0
    const tankRows = block(tanks, tanks <= 2 ? tanks : tanks <= 6 ? 2 : 3, 2.1, 2.3)
    tankRows.forEach(([u, v]) => place("tank", u, v))
    const head = tanks ? Math.min(...tankRows.map(([u]) => u)) - 1.8 : 0
    const infantry = []
    ;[...HUMAN_INFANTRY, ghost].forEach((type) => {
      for (let i = 0; i < (allocation[type] || 0); i += 1) infantry.push(type)
    })
    const perRank = infantry.length > 8 ? 4 : 3
    infantry.forEach((type, i) => {
      const rank = Math.floor(i / perRank)
      const inRank = Math.min(perRank, infantry.length - rank * perRank)
      place(type, head - rank * SPACING, ((i % perRank) - (inRank - 1) / 2) * SPACING)
    })
    // The helicopters fly as a square block on the right flank of what walks, never on top of it:
    // a rotor over a tank read as one figure (owner, 2026-10-26).
    const helicopters = allocation.helicopter || 0
    if (helicopters) {
      const ground = figures.map((figure) => figure.u)
      const middle = ground.length ? (Math.min(...ground) + Math.max(...ground)) / 2 : 0
      const flank = figures.length ? Math.max(...figures.map((figure) => figure.v + figure.size / 2)) : null
      const rows = block(helicopters, Math.ceil(Math.sqrt(helicopters)), 2.3, 2.3)
      const top = Math.max(...rows.map(([u]) => u))
      const bottom = Math.min(...rows.map(([u]) => u))
      const left = Math.min(...rows.map(([, v]) => v))
      // A rotor reaches 0.62 of the helicopter's size from its hub; leave that and a little air.
      const shift = flank == null ? 0 : flank - left + FIGURE_SIZE.helicopter * 0.62 + 0.3
      rows.forEach(([u, v]) => place("helicopter", u - (top + bottom) / 2 + middle, v + shift, { air: true }))
    }
  }

  let minU = Infinity
  let maxU = -Infinity
  let minV = Infinity
  let maxV = -Infinity
  figures.forEach((figure) => {
    const half = figure.size / 2
    minU = Math.min(minU, figure.u - half)
    maxU = Math.max(maxU, figure.u + half)
    minV = Math.min(minV, figure.v - half)
    maxV = Math.max(maxV, figure.v + half)
  })
  const midU = (minU + maxU) / 2
  const midV = (minV + maxV) / 2
  figures.forEach((figure) => {
    figure.u -= midU
    figure.v -= midV
  })
  // What flies is drawn last, over what walks.
  figures.sort((a, b) => (a.air ? 1 : 0) - (b.air ? 1 : 0))
  const formation = { figures, total, headcount, span: Math.max(maxU - minU, maxV - minV, 2) }
  if (cache.size > 500) cache.clear()
  cache.set(cacheKey, formation)
  return formation
}

/* The size of one figure, in px, for a group at a cell size: a quarter of a cell, shrunk so the
   whole group stays within MAX_SPAN_CELLS. */
export const figurePx = (formation, cell) => Math.max(3, Math.min(cell * 0.26, (cell * MAX_SPAN_CELLS) / formation.span))

// === The figures, each drawn facing +x about its own centre at size `s` ===

const INK = "rgba(6, 9, 8, 0.55)"
const SHADOW = "rgba(0, 0, 0, 0.32)"

/* Whose figures these are, on their feet (owner, 2026-10-26, `requirements/36` S6: "it can be a
   little difficult to know if the units are zombies or soldiers just from the look"): while a group
   is drawn with a relation, every figure's ground shadow takes the relation's colour and a little
   more room — green for yours, blue for an ally's, red for the enemy's — and the figures keep
   their own colours. The feet of a whole group are drawn first and its figures over them, so one
   figure's colour never lies on its neighbour; while the figures draw, their own shadows are off. */
const FEET = { self: "rgba(92, 224, 123, 0.72)", ally: "rgba(90, 169, 214, 0.72)", enemy: "rgba(226, 86, 75, 0.72)" }
const DOT = { self: "#5ce07b", ally: "#5aa9d6", enemy: "#e2564b" }
let feetDrawn = false
const shadowOval = (ctx, x, y, rx, ry, color = SHADOW) => {
  if (!feetDrawn) oval(ctx, x, y, rx, ry, color)
}
// One figure's coloured feet, about its own centre: a flier's fall far below it, as its shadow does.
const drawFeet = (ctx, figure, s, color) => {
  const size = s * figure.size
  if (figure.type === "helicopter") oval(ctx, size * 0.35, size * 0.45, size * 0.5, size * 0.22, color)
  else oval(ctx, size * 0.04, size * 0.06, size * 0.5, size * 0.45, color)
}
const HAT = { soldier: "#3f5a2e", ranger: "#7a5a33", elite: "#202426", medic: "#eef0ec", worker: "#e8b53a", ghost_human: "#53634c" }
const BODY = { elite: "#2d3336", ghost_human: "#3c4a38" }
const SHIRT = { zombie: "#5b3c78", walker: "#4d4d6b", cursed: "#7a2f3a", destructor: "#3b2a4f", ambusher: "#343a33", ghost_zombie: "#3f4d3a" }
const SKIN = { ghost_zombie: "#56664c", ambusher: "#6d8a52" }

const roundedRect = (ctx, x, y, w, h, r) => {
  const radius = Math.min(r, w / 2, h / 2)
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.arcTo(x + w, y, x + w, y + h, radius)
  ctx.arcTo(x + w, y + h, x, y + h, radius)
  ctx.arcTo(x, y + h, x, y, radius)
  ctx.arcTo(x, y, x + w, y, radius)
  ctx.closePath()
}

const fillRounded = (ctx, x, y, w, h, r, color) => {
  roundedRect(ctx, x, y, w, h, r)
  ctx.fillStyle = color
  ctx.fill()
}

const disc = (ctx, x, y, r, color, ink = INK) => {
  ctx.beginPath()
  ctx.arc(x, y, r, 0, Math.PI * 2)
  ctx.fillStyle = color
  ctx.fill()
  if (ink) {
    ctx.lineWidth = Math.max(0.6, r * 0.25)
    ctx.strokeStyle = ink
    ctx.stroke()
  }
}

const oval = (ctx, x, y, rx, ry, color) => {
  ctx.beginPath()
  ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2)
  ctx.fillStyle = color
  ctx.fill()
}

const line = (ctx, x1, y1, x2, y2, width, color) => {
  ctx.beginPath()
  ctx.moveTo(x1, y1)
  ctx.lineTo(x2, y2)
  ctx.lineWidth = width
  ctx.strokeStyle = color
  ctx.stroke()
}

const groundShadow = (ctx, s, rx = 0.4, ry = 0.4) => shadowOval(ctx, s * 0.08, s * 0.12, s * rx, s * ry)

const drawInfantry = (ctx, s, t, type, walking) => {
  const step = walking ? Math.sin(t) : 0
  groundShadow(ctx, s)
  // Boots out front and behind as the figure steps.
  fillRounded(ctx, s * (0.05 + step * 0.22), -s * 0.26, s * 0.22, s * 0.16, s * 0.05, "#2a2d22")
  fillRounded(ctx, s * (0.05 - step * 0.22), s * 0.1, s * 0.22, s * 0.16, s * 0.05, "#2a2d22")
  fillRounded(ctx, -s * 0.38, -s * 0.2, s * 0.22, s * 0.4, s * 0.06, type === "medic" ? "#c9c9c0" : "#5b5134")
  fillRounded(ctx, -s * 0.2, -s * 0.36, s * 0.38, s * 0.72, s * 0.16, BODY[type] || "#4f6b3a")
  if (type !== "medic" && type !== "worker" && type !== "ghost_human") {
    line(ctx, 0, s * 0.2, s * 0.62, s * 0.12, Math.max(1, s * 0.09), "#1c1f1e")
  }
  disc(ctx, s * 0.02, 0, s * 0.22, HAT[type] || HAT.soldier)
  if (type === "medic") {
    ctx.fillStyle = "#d33a3a"
    ctx.fillRect(-s * 0.03, -s * 0.12, s * 0.08, s * 0.24)
    ctx.fillRect(-s * 0.1, -s * 0.04, s * 0.22, s * 0.08)
  }
}

const drawZombie = (ctx, s, t, type, walking) => {
  const sway = walking ? Math.sin(t) * s * 0.12 : 0
  const reach = walking ? Math.sin(t * 2) * 0.06 : 0
  groundShadow(ctx, s, 0.4, 0.42)
  ctx.save()
  ctx.translate(0, sway)
  if (type !== "ghost_zombie") {
    ctx.lineCap = "round"
    const arm = Math.max(1, s * 0.13)
    line(ctx, 0, -s * 0.26, s * (0.55 + reach), -s * 0.22, arm, "#6f9a44")
    line(ctx, 0, s * 0.26, s * (0.48 - reach), s * 0.24, arm, "#6f9a44")
    ctx.lineCap = "butt"
  }
  fillRounded(ctx, -s * 0.24, -s * 0.34, s * 0.42, s * 0.68, s * 0.18, SHIRT[type] || SHIRT.zombie)
  disc(ctx, s * 0.06, -s * 0.02, s * 0.2, SKIN[type] || "#8fbf5a")
  ctx.restore()
}

const drawTitan = (ctx, s, t, walking) => {
  const heave = walking ? Math.sin(t) * s * 0.05 : 0
  groundShadow(ctx, s, 0.5, 0.5)
  ctx.save()
  ctx.translate(heave, 0)
  disc(ctx, s * 0.12, -s * 0.38, s * 0.17, "#6f9a44")
  disc(ctx, s * 0.12, s * 0.38, s * 0.17, "#6f9a44")
  fillRounded(ctx, -s * 0.3, -s * 0.42, s * 0.52, s * 0.84, s * 0.26, "#8fbf5a")
  fillRounded(ctx, -s * 0.26, -s * 0.28, s * 0.38, s * 0.56, s * 0.14, "#4a2d63")
  disc(ctx, s * 0.14, 0, s * 0.15, "#8fbf5a")
  ctx.restore()
}

const drawDog = (ctx, s, t, walking) => {
  const gallop = walking ? Math.sin(t * 1.5) : 0
  shadowOval(ctx, s * 0.05, s * 0.1, s * 0.5, s * 0.25)
  ctx.lineCap = "round"
  const leg = Math.max(0.8, s * 0.08)
  line(ctx, s * 0.18, -s * 0.12, s * (0.18 + gallop * 0.14), -s * 0.24, leg, "#4a3c30")
  line(ctx, s * 0.18, s * 0.12, s * (0.18 - gallop * 0.14), s * 0.24, leg, "#4a3c30")
  line(ctx, -s * 0.2, -s * 0.12, s * (-0.2 - gallop * 0.14), -s * 0.24, leg, "#4a3c30")
  line(ctx, -s * 0.2, s * 0.12, s * (-0.2 + gallop * 0.14), s * 0.24, leg, "#4a3c30")
  line(ctx, -s * 0.4, 0, -s * 0.6, gallop * s * 0.1, leg, "#4a3c30")
  ctx.lineCap = "butt"
  oval(ctx, 0, 0, s * 0.42, s * 0.16, "#6b5a4a")
  ctx.fillStyle = "#7d3b34"
  ctx.fillRect(-s * 0.1, -s * 0.06, s * 0.14, s * 0.1)
  oval(ctx, s * 0.45, 0, s * 0.15, s * 0.11, "#4a3c30")
}

const drawTank = (ctx, s, t, walking) => {
  shadowOval(ctx, s * 0.06, s * 0.08, s * 0.5, s * 0.38)
  const length = s * 0.95
  const width = s * 0.62
  const tread = s * 0.15
  fillRounded(ctx, -length / 2, -width / 2, length, tread, s * 0.04, "#1f2420")
  fillRounded(ctx, -length / 2, width / 2 - tread, length, tread, s * 0.04, "#1f2420")
  // The track links roll while it moves.
  const link = s * 0.1
  const offset = walking ? (((t * s * 0.05) % link) + link) % link : 0
  ctx.beginPath()
  for (let x = -length / 2 + offset; x < length / 2; x += link) {
    ctx.moveTo(x, -width / 2)
    ctx.lineTo(x, -width / 2 + tread)
    ctx.moveTo(x, width / 2 - tread)
    ctx.lineTo(x, width / 2)
  }
  ctx.lineWidth = Math.max(0.6, s * 0.03)
  ctx.strokeStyle = "#3a4038"
  ctx.stroke()
  fillRounded(ctx, -length * 0.44, -width * 0.33, length * 0.88, width * 0.66, s * 0.06, "#566b45")
  line(ctx, s * 0.05, 0, s * 0.7, 0, Math.max(1, s * 0.07), "#1c1f1e")
  disc(ctx, -s * 0.02, 0, s * 0.18, "#3b4a2f")
}

const drawHelicopter = (ctx, s, time) => {
  // The shadow far below and off to one side: it flies.
  ctx.save()
  ctx.translate(s * 0.35, s * 0.45)
  if (!feetDrawn) oval(ctx, 0, 0, s * 0.42, s * 0.16, "rgba(0, 0, 0, 0.26)")
  ctx.fillStyle = "rgba(0, 0, 0, 0.26)"
  ctx.fillRect(-s * 0.85, -s * 0.03, s * 0.5, s * 0.06)
  ctx.restore()
  ctx.save()
  ctx.translate(0, Math.sin(time * 0.7) * s * 0.04)
  ctx.fillStyle = "#36472c"
  ctx.fillRect(-s * 0.85, -s * 0.04, s * 0.55, s * 0.08)
  ctx.fillRect(-s * 0.88, -s * 0.16, s * 0.06, s * 0.32)
  oval(ctx, 0, 0, s * 0.4, s * 0.17, "#4d6340")
  oval(ctx, s * 0.24, 0, s * 0.13, s * 0.11, "#9fc6d6")
  oval(ctx, 0, 0, s * 0.62, s * 0.62, "rgba(220, 230, 220, 0.10)")
  const blade = time * 6
  ctx.beginPath()
  for (let i = 0; i < 2; i += 1) {
    const angle = blade + (i * Math.PI) / 2
    ctx.moveTo(-Math.cos(angle) * s * 0.62, -Math.sin(angle) * s * 0.62)
    ctx.lineTo(Math.cos(angle) * s * 0.62, Math.sin(angle) * s * 0.62)
  }
  ctx.lineWidth = Math.max(0.8, s * 0.05)
  ctx.strokeStyle = "rgba(30, 34, 30, 0.85)"
  ctx.stroke()
  disc(ctx, 0, 0, s * 0.05, "#222", null)
  ctx.restore()
}

const drawFigure = (ctx, figure, s, time, walking) => {
  const t = time * 7 * figure.rate + figure.phase
  const size = s * figure.size
  switch (figure.type) {
    case "tank":
      return drawTank(ctx, size, t, walking)
    case "helicopter":
      return drawHelicopter(ctx, size, time)
    case "titan":
      return drawTitan(ctx, size, t * 0.3, walking)
    case "dog":
      return drawDog(ctx, size, t, walking)
    default:
      if (ZOMBIE_BODIES.includes(figure.type) || figure.type === "ghost_zombie") {
        return drawZombie(ctx, size, t * 0.55, figure.type, walking)
      }
      return drawInfantry(ctx, size, t, figure.type, walking)
  }
}

/* How far a walking figure drifts from its place in the group, in figure spacings (owner,
   2026-10-26: "sorting the units a little bit while walking"): each at its own rhythm, so the group
   reads as bodies on the move rather than a picture sliding. The horde is loose and drifts more;
   the ranks keep their places, and a tank or a helicopter barely moves out of its line. */
// Doubled, with a sway of the heading added, at the owner's ask (2026-10-26: "more noticeable");
// the helicopters kept theirs, and a tank does not wiggle at all (owner, the same day: "Tanks don't
// wiggle. Same as helicopters") — the column holds its line and only its tracks roll.
const SHUFFLE = { tank: 0, helicopter: 0.1, titan: 0.15, dog: 0.4, soldier: 0.2, ranger: 0.2, elite: 0.16, medic: 0.2, worker: 0.2, ghost_human: 0.2 }
const SWAY = { tank: 0, helicopter: 0, titan: 0.12, dog: 0.2, soldier: 0.12, ranger: 0.12, elite: 0.1, medic: 0.12, worker: 0.12, ghost_human: 0.12 }
const shuffleOf = (figure, time) => {
  const reach = SHUFFLE[figure.type] ?? 0.35
  const t = time * figure.rate
  return [Math.sin(t * 3.1 + figure.phase) * reach, Math.cos(t * 2.3 + figure.phase * 1.7) * reach]
}
const swayOf = (figure, time) => Math.sin(time * figure.rate * 4.3 + figure.phase * 2.1) * (SWAY[figure.type] ?? 0.25)

/* The group about (`cx`, `cy`) in px, facing `angle` (radians, 0 is east, screen y down). */
export const drawFormation = (ctx, { formation, team, cx, cy, cell, angle = 0, time = 0, walking = false, alpha = 1, relation = null }) => {
  if (!formation || formation.figures.length === 0) return
  const s = figurePx(formation, cell)
  ctx.save()
  ctx.globalAlpha = Math.max(0, Math.min(1, alpha))
  ctx.translate(cx, cy)
  ctx.rotate(angle)
  if (cell < DOTS_BELOW_CELL_PX) {
    // A dot is too small for feet: it takes the relation's colour itself (S6).
    const color = DOT[relation] || (team === "zombie" ? "#9acb5d" : "#c9d6a8")
    formation.figures.forEach((figure) => {
      disc(ctx, figure.u * s * SPACING * 0.7, figure.v * s * SPACING * 0.7, Math.max(1, cell * 0.07 * (figure.size > 1 ? 1.6 : 1)), color, "rgba(6, 9, 8, 0.6)")
    })
  } else {
    const placeFigure = (figure) => {
      const [du, dv] = walking ? shuffleOf(figure, time) : [0, 0]
      ctx.translate((figure.u + du) * s, (figure.v + dv) * s)
      const turn = (figure.tilt || 0) + (walking ? swayOf(figure, time) : 0)
      if (turn) ctx.rotate(turn)
    }
    const feet = FEET[relation]
    if (feet) {
      formation.figures.forEach((figure) => {
        ctx.save()
        placeFigure(figure)
        drawFeet(ctx, figure, s, feet)
        ctx.restore()
      })
    }
    feetDrawn = Boolean(feet)
    formation.figures.forEach((figure) => {
      ctx.save()
      placeFigure(figure)
      drawFigure(ctx, figure, s, time, walking)
      ctx.restore()
    })
    feetDrawn = false
  }
  ctx.restore()
}

// === A battle on a cell (owner, 2026-10-26, `requirements/36` B1–B4) ===

/* Who leads a side into the fight: the heavy units and the dogs at the meeting line, the ranks
   behind them; helicopters fly over the back. */
const BATTLE_FRONT = { tank: 0, titan: 0, destructor: 0, dog: 0, ranger: 2, medic: 2, worker: 3 }
// Six a side: with more, each figure is a tenth of the cell and a city's icon swallows them.
const BATTLE_FIGURES = 6
const battleCache = new Map()

/* The two armies of a battle as figures, one side per team: `armies` is `{human, zombie}`, each the
   side's units (allies merged), `{}` for a side the viewer cannot read — drawn as the side's plain
   figures — and null for a side not there. More figures for a bigger army, a heavy unit's weight,
   as `formationFor` gives a stack; capped lower, since a side has half a cell. Pure and cached. */
export const battleFor = (armies) => {
  const cacheKey = JSON.stringify(armies || {})
  if (battleCache.has(cacheKey)) return battleCache.get(cacheKey)
  const sides = ["human", "zombie"].flatMap((team) => {
    const units = armies?.[team]
    if (units == null) return []
    const bodies = Object.entries(units)
      .filter(([unit]) => unit !== "count")
      .map(([unit, value]) => [unit, countOf(value)])
      .filter(([, n]) => n > 0)
    const total = bodies.length ? bodies.reduce((sum, [, n]) => sum + n, 0) : countOf(units.count)
    // The stack's own count (3 to MAX_FIGURES) scaled into 2 to BATTLE_FIGURES, so a bigger army
    // still has more figures in its half.
    const count = 2 + Math.round(((BATTLE_FIGURES - 2) * (figuresFor(Math.max(1, total)) - 3)) / (MAX_FIGURES - 3))
    const ghost = team === "zombie" ? "ghost_zombie" : "ghost_human"
    const allocation = bodies.length ? allocateFigures(bodies, count) : { [ghost]: count }
    const random = seeded(seedOf(`${team}|${cacheKey}`))
    const types = Object.entries(allocation).flatMap(([type, n]) => Array.from({ length: n }, () => type))
    const figures = types.map((type) => ({ type, size: FIGURE_SIZE[type] || 1, phase: random() * Math.PI * 2, rate: 0.8 + random() * 0.5 }))
    return [{ team, dir: team === "zombie" ? 1 : -1, figures }]
  })
  const battle = { sides }
  if (battleCache.size > 300) battleCache.clear()
  battleCache.set(cacheKey, battle)
  return battle
}

/* Who shoots, and from how far in front of the figure the barrel is (in figure sizes): humanity's
   guns. The horde has none — it bites, and its blows are the impact flashes. */
const GUNS = {
  soldier: { reach: 0.62, heavy: false },
  ranger: { reach: 0.62, heavy: false },
  elite: { reach: 0.62, heavy: false },
  tank: { reach: 0.72, heavy: true },
}

/* Every gun fires at its own rhythm (owner, 2026-10-26: "firing sparks"): a muzzle flash at the
   barrel and a short tracer flying at the other side; a tank's shot is bigger and leaves a puff. */
const drawShots = (ctx, guns, cx, cell, time) => {
  guns.forEach(({ x, y, dir, figure, heavy }) => {
    const period = heavy ? 2.2 : 1.1 + (figure.rate - 0.8) * 1.2
    const f = ((time + figure.phase) / period) % 1
    if (f > 0.22) return
    const k = f / 0.22
    const towards = -dir
    // The muzzle flash: a short bright star, gone in the first third of the shot.
    if (k < 0.35) {
      const r = cell * (heavy ? 0.09 : 0.05) * (1 - k)
      ctx.fillStyle = `rgba(255, 230, 140, ${1 - k / 0.35})`
      ctx.beginPath()
      ctx.moveTo(x + towards * r * 2.2, y)
      ctx.lineTo(x, y - r)
      ctx.lineTo(x - towards * r * 0.6, y)
      ctx.lineTo(x, y + r)
      ctx.closePath()
      ctx.fill()
      disc(ctx, x, y, r * 0.6, `rgba(255, 255, 235, ${1 - k / 0.35})`, null)
    }
    // The tracer: a streak from the barrel towards the other side's middle.
    const reach = Math.abs(cx + towards * cell * 0.25 - x)
    const head = x + towards * reach * k
    const tail = x + towards * reach * Math.max(0, k - 0.3)
    ctx.strokeStyle = `rgba(255, 220, 120, ${0.9 * (1 - k * 0.5)})`
    ctx.lineWidth = Math.max(1, cell * (heavy ? 0.035 : 0.02))
    ctx.lineCap = "round"
    ctx.beginPath()
    ctx.moveTo(tail, y)
    ctx.lineTo(head, y)
    ctx.stroke()
    ctx.lineCap = "butt"
  })
}

/* Smoke over the fight: soft grey puffs born where the sides meet, rising and drifting away as they
   grow and fade — one way only, and soft-edged, so they read as smoke and not as dots. */
const drawSmoke = (ctx, cx, cy, cell, time) => {
  const puffs = 7
  const life = 2.8
  for (let i = 0; i < puffs; i += 1) {
    const cycle = time / life + i / puffs
    const age = cycle % 1
    const random = seeded(i * 211 + Math.floor(cycle) * 7907)
    const x = cx + (random() - 0.5) * cell * 0.35 + age * cell * 0.18
    const y = cy + (random() - 0.3) * cell * 0.3 - age * cell * 0.75
    const r = cell * (0.1 + age * 0.26)
    const alpha = 0.32 * (1 - age) * Math.min(1, age * 5)
    const grey = 90 + Math.floor(random() * 30)
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, r)
    gradient.addColorStop(0, `rgba(${grey}, ${grey}, ${grey}, ${alpha})`)
    gradient.addColorStop(1, `rgba(${grey}, ${grey}, ${grey}, 0)`)
    ctx.fillStyle = gradient
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fill()
  }
}

/* The impact flashes where the two sides meet: short four-pointed bursts that pop and fade. */
const drawFlashes = (ctx, cx, cy, cell, time) => {
  for (let i = 0; i < 5; i += 1) {
    const cycle = time * 1.3 + i * 0.61
    const f = cycle % 1
    if (f > 0.35) continue
    const random = seeded(i * 97 + Math.floor(cycle))
    const x = cx + (random() - 0.5) * cell * 0.4
    const y = cy + (random() - 0.5) * cell * 0.6
    const k = f / 0.35
    const len = cell * (0.06 + k * 0.12)
    ctx.strokeStyle = `rgba(255, 236, 170, ${1 - k})`
    ctx.lineWidth = Math.max(1, cell * 0.03)
    ctx.lineCap = "round"
    ctx.beginPath()
    for (let j = 0; j < 4; j += 1) {
      const a = (j * Math.PI) / 2 + Math.PI / 4
      ctx.moveTo(x + Math.cos(a) * len * 0.3, y + Math.sin(a) * len * 0.3)
      ctx.lineTo(x + Math.cos(a) * len, y + Math.sin(a) * len)
    }
    ctx.stroke()
    ctx.lineCap = "butt"
    disc(ctx, x, y, Math.max(1, cell * 0.035) * (1 - k * 0.5), `rgba(255, 255, 230, ${1 - k})`, null)
  }
}

/* A battle on the cell centred at (`cx`, `cy`): each army in its half — the humans west, the horde
   east — facing the other and lunging at it, with impact flashes where they meet. Sized so the
   fight stays about one cell wide. Under `DOTS_BELOW_CELL_PX` a side is a few dots in its colour. */
export const drawBattle = (ctx, { battle, cx, cy, cell, time = 0, relations = {} }) => {
  if (!battle?.sides?.length) return
  ctx.save()
  // Where each gun is, for the shots drawn over the armies (below).
  const guns = []
  battle.sides.forEach(({ dir, figures, team }) => {
    const air = figures.filter((figure) => figure.type === "helicopter")
    const ground = figures
      .filter((figure) => figure.type !== "helicopter")
      .sort((a, b) => (BATTLE_FRONT[a.type] ?? 1) - (BATTLE_FRONT[b.type] ?? 1))
    const perCol = ground.length > 6 ? 3 : 2
    const cols = Math.max(1, Math.ceil(ground.length / perCol))
    const half = cell * 0.52
    const gap = cell * 0.04
    const colW = (half - gap) / Math.max(cols, 2)
    const rowH = (cell * 0.8) / perCol
    const s = Math.max(3, Math.min(cell * 0.17, colW * 0.95, rowH * 0.9))
    const face = dir < 0 ? 0 : Math.PI
    if (cell < DOTS_BELOW_CELL_PX) {
      const color = DOT[relations[team]] || (team === "zombie" ? "#9acb5d" : "#c9d6a8")
      ground.concat(air).slice(0, 4).forEach((_, i) => {
        disc(ctx, cx + dir * cell * (0.14 + (i % 2) * 0.16), cy + (Math.floor(i / 2) - 0.5) * cell * 0.3, Math.max(1, cell * 0.07), color, "rgba(6, 9, 8, 0.6)")
      })
      return
    }
    const placed = ground.map((figure, i) => {
      const col = Math.floor(i / perCol)
      const row = i % perCol
      const inCol = Math.min(perCol, ground.length - col * perCol)
      const lunge = Math.sin(time * 2.6 + figure.phase) * s * 0.22
      const x = cx + dir * (gap + colW * (col + 0.5)) - dir * lunge
      const y = cy + (row - (inCol - 1) / 2) * rowH
      const size = figure.size * (figure.size > 1 ? 0.85 : 1)
      if (GUNS[figure.type]) guns.push({ x: x - dir * s * size * GUNS[figure.type].reach, y, dir, figure, heavy: GUNS[figure.type].heavy })
      return { figure: { ...figure, size }, x, y }
    })
    air.forEach((figure, i) => {
      const x = cx + dir * half * 0.75
      const y = cy + (i - (air.length - 1) / 2) * cell * 0.32
      guns.push({ x: x - dir * s * 0.55, y, dir, figure, heavy: false })
      placed.push({ figure: { ...figure, size: 1.3 }, x, y })
    })
    // Each army on its side's feet (S6), all of them before any figure, as a stack's are.
    const feet = FEET[relations[team]]
    const at = ({ x, y }) => {
      ctx.translate(x, y)
      ctx.rotate(face)
    }
    if (feet) {
      placed.forEach((one) => {
        ctx.save()
        at(one)
        drawFeet(ctx, one.figure, s, feet)
        ctx.restore()
      })
    }
    feetDrawn = Boolean(feet)
    placed.forEach((one) => {
      ctx.save()
      at(one)
      drawFigure(ctx, one.figure, s, time, true)
      ctx.restore()
    })
    feetDrawn = false
  })
  if (cell >= DOTS_BELOW_CELL_PX && battle.sides.length > 1) {
    drawShots(ctx, guns, cx, cell, time)
    drawFlashes(ctx, cx, cy, cell, time)
    drawSmoke(ctx, cx, cy, cell, time)
  }
  ctx.restore()
}
